/**
 * `/login` — the three patient sign-in methods on one screen (M2.9, FR-PORTAL-01,
 * ADR-004, SECURITY.md §4.1).
 *
 * ## Why one screen and not three
 *
 * A patient books once or twice a year. Whichever way they signed in last time, they
 * will not remember it, so the screen offers all three and defaults to the one that
 * needs nothing remembered at all: **a code by email**. Password is second because it
 * is the one people expect to find; the secure link is third because it is the slowest
 * (leave the app, open mail, come back) even though it is the least effort.
 *
 * ## The rule that shapes every message here
 *
 * Nothing on this screen may reveal whether an address has an account (rules §21.4).
 * "We've sent you a code" and "Check your email" are shown unconditionally; the API
 * answers `202`/`200` either way and a wrong password, an unknown address and a
 * disabled account all come back as the same `401`. A locked account comes back as
 * `429` with `Retry-After`, deliberately identical to a rate limit, so the word
 * "locked" appears nowhere in this file (SECURITY.md §4.4).
 *
 * ## Staff
 *
 * `POST /auth/sign-in/email` for an MFA-enrolled account returns
 * `{ twoFactorRedirect: true }` and **no session**. The patient app has no second-factor
 * screen, so it says so plainly rather than appearing to sign someone in and then
 * finding them anonymous.
 *
 * The component takes its navigation as a prop, so it renders in a test without a
 * router — the same shape as `BookPage` (M5.3).
 */

import { useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';

import { SESSION_QUERY_KEY, useApiClient } from '@platform/api-client/react';
import { Alert, Button, Field, Input } from '@platform/ui';

import {
  AuthCard,
  ErrorSummary,
  ModeTabs,
  OtpInput,
  PasswordInput,
  TabPanel,
  useCooldown,
} from './components';

/** Which method the screen opens on. `code` is the default (see the note above). */
export type LoginMode = 'code' | 'password' | 'link';

export const LOGIN_MODES: LoginMode[] = ['code', 'password', 'link'];

export function isLoginMode(value: unknown): value is LoginMode {
  return typeof value === 'string' && (LOGIN_MODES as string[]).includes(value);
}

/** Seconds before "Send a new code" becomes available again. */
const RESEND_COOLDOWN_SECONDS = 30;

export interface LoginPageProps {
  /** Which tab to open. */
  initialMode?: LoginMode;
  /** Where the patient was heading. Passed to the secure link so it lands there too. */
  next?: string;
  /** Called once a session exists. The route navigates; this component does not. */
  onSignedIn: () => void;
  /** Called for "Forgot password?" and "Create an account". */
  onNavigate: (path: string) => void;
}

export function LoginPage({ initialMode = 'code', next, onSignedIn, onNavigate }: LoginPageProps) {
  const api = useApiClient();
  const queryClient = useQueryClient();

  const [mode, setMode] = useState<LoginMode>(initialMode);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [linkSent, setLinkSent] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [pending, setPending] = useState(false);

  const cooldown = useCooldown(RESEND_COOLDOWN_SECONDS);

  /** Every submit goes through here, so no path can forget to clear the last error. */
  async function run(work: () => Promise<void>): Promise<void> {
    setError(null);
    setPending(true);
    try {
      await work();
    } catch (caught) {
      setError(caught);
    } finally {
      setPending(false);
    }
  }

  /** A session now exists; refresh the cached summary before handing over to the route. */
  async function finishSignIn(): Promise<void> {
    await queryClient.invalidateQueries({ queryKey: SESSION_QUERY_KEY, refetchType: 'all' });
    onSignedIn();
  }

  function switchMode(value: LoginMode): void {
    setMode(value);
    setError(null);
    setNotice(null);
  }

  /* -------------------------------------------------- code -------------- */

  const sendCode = (event: FormEvent) => {
    event.preventDefault();
    void run(async () => {
      await api.auth.sendEmailCode(email);
      setCodeSent(true);
      setNotice(null);
      cooldown.start();
    });
  };

  const resendCode = () => {
    void run(async () => {
      await api.auth.sendEmailCode(email);
      setNotice('We sent another code.');
      cooldown.start();
    });
  };

  const verifyCode = (event: FormEvent) => {
    event.preventDefault();
    void run(async () => {
      await api.auth.signInWithEmailCode({ email, otp: otp.trim() });
      await finishSignIn();
    });
  };

  /* ---------------------------------------------- password -------------- */

  const signInWithPassword = (event: FormEvent) => {
    event.preventDefault();
    void run(async () => {
      const result = await api.auth.signInWithPassword({ email, password });
      if (result.twoFactorRedirect === true) {
        // No session was issued. Saying so is the honest outcome; the clinic admin app
        // is where the second factor lives (SECURITY.md §4.3).
        setNotice(
          'This account signs in through the clinic admin app, which asks for a second factor.',
        );
        return;
      }
      await finishSignIn();
    });
  };

  /* -------------------------------------------------- link -------------- */

  const sendLink = (event: FormEvent) => {
    event.preventDefault();
    void run(async () => {
      await api.auth.requestSecureLink({
        email,
        ...(next === undefined ? {} : { callbackPath: next }),
      });
      setLinkSent(true);
    });
  };

  /* ------------------------------------------------- render ------------- */

  return (
    <AuthCard
      title="Sign in"
      lede="Choose how you would like to sign in."
      footer={
        <>
          New here?{' '}
          <a
            href="/register"
            onClick={(event) => {
              event.preventDefault();
              onNavigate('/register');
            }}
            className="text-primary-accessible underline underline-offset-2"
          >
            Create an account
          </a>
        </>
      }
    >
      <ModeTabs
        label="Sign-in method"
        value={mode}
        onChange={switchMode}
        tabs={[
          { value: 'code', label: 'Email me a code' },
          { value: 'password', label: 'Password' },
          { value: 'link', label: 'Send me a link' },
        ]}
      />

      <ErrorSummary error={error} />

      {notice === null ? null : (
        <div className="mb-4">
          <Alert tone="info">{notice}</Alert>
        </div>
      )}

      {mode === 'code' ? (
        <TabPanel value="code">
          {codeSent ? (
            <form onSubmit={verifyCode} noValidate>
              <p className="mb-4 text-base leading-[1.5] text-neutral-500">
                We sent a 6-digit code to <strong className="text-ink">{email}</strong>. It is good
                for 10 minutes.
              </p>
              <Field label="6-digit code" required>
                <OtpInput
                  name="otp"
                  value={otp}
                  onChange={(event) => {
                    setOtp(event.target.value.replace(/\D/g, ''));
                  }}
                  autoFocus
                />
              </Field>
              <Button
                type="submit"
                size="lg"
                block
                className="mt-5"
                loading={pending}
                loadingLabel="Signing you in"
                disabled={otp.trim().length < 6}
              >
                Sign in
              </Button>
              <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  className="min-h-11"
                  onClick={resendCode}
                  disabled={pending || cooldown.remaining > 0}
                >
                  {cooldown.remaining > 0
                    ? `Send a new code in ${cooldown.remaining}s`
                    : 'Send a new code'}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="min-h-11"
                  onClick={() => {
                    setCodeSent(false);
                    setOtp('');
                    setNotice(null);
                    setError(null);
                  }}
                >
                  Use a different email
                </Button>
              </div>
            </form>
          ) : (
            <form onSubmit={sendCode} noValidate>
              <Field label="Email address" required>
                <Input
                  name="email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(event) => {
                    setEmail(event.target.value);
                  }}
                  placeholder="you@example.com"
                />
              </Field>
              <Button
                type="submit"
                size="lg"
                block
                className="mt-5"
                loading={pending}
                loadingLabel="Sending your code"
                disabled={email.trim() === ''}
              >
                Email me a code
              </Button>
            </form>
          )}
        </TabPanel>
      ) : null}

      {mode === 'password' ? (
        <TabPanel value="password">
          <form onSubmit={signInWithPassword} noValidate>
            <Field label="Email address" required className="mb-4">
              <Input
                name="email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(event) => {
                  setEmail(event.target.value);
                }}
                placeholder="you@example.com"
              />
            </Field>
            <Field label="Password" required>
              <PasswordInput
                name="password"
                autoComplete="current-password"
                value={password}
                onChange={(event) => {
                  setPassword(event.target.value);
                }}
              />
            </Field>
            <p className="mt-2">
              <a
                href="/reset-password"
                onClick={(event) => {
                  event.preventDefault();
                  onNavigate('/reset-password');
                }}
                className="inline-flex min-h-11 items-center text-base text-primary-accessible underline underline-offset-2"
              >
                Forgot password?
              </a>
            </p>
            <Button
              type="submit"
              size="lg"
              block
              className="mt-2"
              loading={pending}
              loadingLabel="Signing you in"
              disabled={email.trim() === '' || password === ''}
            >
              Sign in
            </Button>
          </form>
        </TabPanel>
      ) : null}

      {mode === 'link' ? (
        <TabPanel value="link">
          {linkSent ? (
            /*
             * The confirmation is identical whether or not that address has an account.
             * It names the address the patient typed - which they already know - and
             * nothing else (rules §21.4).
             */
            <div>
              <Alert tone="success" title="Check your email.">
                If <strong>{email}</strong> has an account, a sign-in link is on its way. The link
                works once and expires in 15 minutes.
              </Alert>
              <Button
                variant="secondary"
                size="lg"
                block
                className="mt-5"
                onClick={() => {
                  setLinkSent(false);
                }}
              >
                Use a different email
              </Button>
            </div>
          ) : (
            <form onSubmit={sendLink} noValidate>
              <Field
                label="Email address"
                required
                help="We will email you a link that signs you in. No password needed."
              >
                <Input
                  name="email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(event) => {
                    setEmail(event.target.value);
                  }}
                  placeholder="you@example.com"
                />
              </Field>
              <Button
                type="submit"
                size="lg"
                block
                className="mt-5"
                loading={pending}
                loadingLabel="Sending your link"
                disabled={email.trim() === ''}
              >
                Email me a sign-in link
              </Button>
            </form>
          )}
        </TabPanel>
      ) : null}
    </AuthCard>
  );
}
